import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import type { ElementLabelConfig } from '../../elements/label';
import { StylePropertyEditorComponent } from './style-property-editor.component';
import { LabelEditorComponent } from './label-editor.component';
import { STYLE_SCHEMAS, type StyleKeyType, type StylePropertySchema } from './style-property-schema.model';

/**
 * Collapsible section for one style type (point/line/polygon/angle/circle/curve).
 * Shows a 2-column grid of property controls and an optional label editor.
 */
@Component({
  selector: 'dojo-style-section',
  standalone: true,
  imports: [StylePropertyEditorComponent, LabelEditorComponent, MatSlideToggleModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <details class="group rounded-md bg-sky-50 p-2">
      <summary
        class="flex cursor-pointer select-none items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-neutral-400"
      >
        <span class="transition-transform group-open:rotate-90">&#9654;</span>
        {{ name() }}
        @if (showVisible()) {
          <mat-slide-toggle
            class="ml-auto scale-75"
            [checked]="isVisible()"
            (click)="$event.stopPropagation()"
            (change)="visibleChange.emit($event.checked)"
          />
        }
      </summary>

      <div class="mt-1.5 grid grid-cols-2 gap-1.5">
        @for (prop of properties(); track prop.key) {
          <dojo-style-property-editor
            [schema]="prop"
            [value]="getPropertyValue(prop.key)"
            [defaultValue]="getDefaultValue(prop.key)"
            (valueChange)="onPropertyChange(prop.key, $event)"
          />
        }
      </div>

      @if (showLabel()) {
        <div class="mt-2 border-t border-neutral-100 pt-1.5">
          <div class="mb-1 text-[9px] font-semibold uppercase tracking-wider text-neutral-300">Label</div>
          <dojo-label-editor
            [labelConfig]="labelValue()"
            [styleKey]="styleKey()"
            (labelConfigChange)="labelChange.emit($event)"
          />
        </div>
      }
    </details>
  `,
})
export class StyleSectionComponent {
  readonly name = input.required<string>();
  readonly styleKey = input.required<StyleKeyType>();
  readonly styleData = input<Record<string, unknown>>({});
  readonly defaults = input<Record<string, unknown>>({});
  readonly showLabel = input(false);
  readonly showVisible = input(false);
  readonly visibleValue = input<boolean | undefined>();
  readonly labelValue = input<ElementLabelConfig | boolean | undefined>();

  readonly styleChange = output<{ key: string; value: unknown }>();
  readonly labelChange = output<ElementLabelConfig | undefined>();
  readonly visibleChange = output<boolean>();

  readonly properties = computed<readonly StylePropertySchema[]>(() => {
    const schema = STYLE_SCHEMAS[this.styleKey()];
    return schema?.properties ?? [];
  });

  readonly isVisible = computed(() => this.visibleValue() ?? true);

  getPropertyValue(key: string): unknown {
    return this.styleData()[key];
  }

  getDefaultValue(key: string): unknown {
    return this.defaults()[key];
  }

  onPropertyChange(key: string, value: unknown): void {
    this.styleChange.emit({ key, value });
  }
}
